import React from 'react';

import { CommodityPhotoCover } from '@components/utilities/Commodity/CommodityPhotoCover';
import { commodityStrings } from '@data/commodities';
import { Commodity } from '@models/next/Commodity';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Link, useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    edit: '編集',
    count: (count: number) => `${count}枚`,
  },
});

export interface CommodityCardProps {
  editUrl?: string;
  detailsUrl?: string;
  commodity: Commodity;
}

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    alignItems: 'stretch',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey[50],
    borderRadius: 2,
  },
  cardLinkDetails: {
    cursor: 'pointer',
  },
  bar: {
    backgroundColor: theme.palette.primary.main,
    minWidth: 11.67,
    minHeight: 140,
    borderTopLeftRadius: 2,
    borderBottomLeftRadius: 2,
  },
  content: {
    flex: 1,
    padding: 10,
    minHeight: 140,
    ...common.flexInline,
    ...common.justifyStart,
    width: '100%',
  },
  contentMainCol: {
    ...common.flexColumn,
    flex: 1,
    height: '100%',
    paddingRight: '87px',
    overflow: 'hidden',
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      paddingRight: '10px',
    },
  },
  contentMainHead: {
    ...common.flexInline,
    height: 40,
    alignItems: 'center',
  },
  commodityNameCol: {
    ...common.middleText,
    flex: 1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    color: theme.palette.text.primary,
    fontWeight: 'bold',
  },
  commodityNoCol: {
    ...common.middleText,
    flex: 1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    color: theme.palette.text.primary,
    fontWeight: 'bold',
  },
  contentMainBody: {
    flex: 1,
    ...common.flexColumn,
    ...common.middleText,
    color: theme.palette.grey[800],
    width: '100%',
  },
  commodityDesc: {
    ...common.middleText,
    ...common.wordBreakBreakAll,
    overflow: 'hidden',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    display: '-webkit-box',
    textOverflow: 'ellipsis',
    color: theme.palette.grey[700],
  },
  contentPreviewCol: {
    maxWidth: 99,
    height: '100%',
    backgroundColor: '',
    ...common.flexColumnCenter,
    ...common.justifyCenter,
  },
  contentEditCol: {
    width: 83,
    height: '100%',
    paddingTop: '4px',
    ...common.middleText,
    ...common.flexInline,
    ...common.justifyCenter,
    [theme.breakpoints.down('sm')]: {
      width: 50,
    },
  },
  editBtn: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
  commodityFieldEmpty: {
    color: theme.palette.grey[500],
  },
}));

export const CommodityCard: React.FC<CommodityCardProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { commodity, editUrl, detailsUrl } = props;

  const { name_en, commodity_no, explanation } = commodity;

  const history = useHistory();

  return (
    <div data-testid="CommodityCard" className={clsx(classes.container)}>
      <div className={classes.bar} />

      <div className={classes.content}>
        <div
          className={classes.contentMainCol}
          onClick={() => {
            if (detailsUrl) {
              history.push(detailsUrl);
            }
          }}
        >
          <div className={classes.contentMainHead}>
            <div
              className={clsx(classes.commodityNameCol, !name_en && classes.commodityFieldEmpty)}
            >
              {name_en || `${commodityStrings.name_en}...`}
            </div>
            <div
              className={clsx(classes.commodityNoCol, !commodity_no && classes.commodityFieldEmpty)}
            >
              {commodity_no || `${commodityStrings.commodity_no}...`}
            </div>
          </div>
          <div className={clsx(classes.contentMainBody)}>
            <div
              className={clsx(classes.commodityDesc, !explanation && classes.commodityFieldEmpty)}
              title={explanation || `${commodityStrings.explanation}...`}
            >
              {explanation || `${commodityStrings.explanation}...`}
            </div>
          </div>
        </div>
        <div
          className={classes.contentPreviewCol}
          onClick={() => {
            if (detailsUrl) {
              history.push(detailsUrl);
            }
          }}
        >
          <CommodityPhotoCover commodity={commodity} />
        </div>
        {editUrl && (
          <div className={classes.contentEditCol}>
            <Link to={editUrl || '#'} className={classes.editBtn}>
              {strings.edit}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
